'use client';

import Link from 'next/link';
import { usePathname } from 'next/navigation';
import Icon from '@/components/ui/AppIcon';
import FAQAccordion from '@/components/common/FAQAccordion';
import { tools } from '@/config/tools';

interface Step {
  step: number;
  title: string;
  desc: string;
}

interface Feature {
  icon: string;
  title: string;
  desc: string;
  color?: string;
  bg?: string;
}

interface FAQ {
  q: string;
  a: string;
}

interface PDFToolContentProps {
  title: string;
  intro: string[];

  stepSectionTitle: string;
  stepCircleColor: string;
  steps: Step[];

  featureSectionTitle: string;
  featureIntro?: string;
  features: Feature[];

  useCaseSectionTitle: string;
  useCaseIntro?: string;
  useCases?: string[];

  securitySectionTitle?: string;
  security?: string[];

  faqSectionTitle: string;
  faqs: FAQ[];
}

export default function PDFToolContent({
  title,
  intro,
  stepSectionTitle,
  stepCircleColor,
  steps,
  featureSectionTitle,
  featureIntro,
  features,
  useCaseSectionTitle,
  useCaseIntro,
  useCases,
  securitySectionTitle,
  security,
  faqSectionTitle,
  faqs,
}: PDFToolContentProps) {
  const pathname = usePathname();

  // Filter out the current tool and shuffle/slice if needed,
  // but here we just show all other tools.
  const filteredTools = tools.filter((t) => t.href !== pathname);

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6">
      {/* ================= INTRO ================= */}
      <article className="py-16">
        <div className="prose-custom max-w-none">
          <h2 className="font-heading font-extrabold mb-6 text-[clamp(22px,3vw,32px)] text-brand-dark tracking-[-0.02em]">
            {title}
          </h2>

          {intro.map((p, i) => (
            <p
              key={i}
              className={`text-brand-mid text-[15.5px] leading-[1.75] font-body ${i === intro.length - 1 ? 'mb-6' : 'mb-5'}`}
            >
              {p}
            </p>
          ))}

          {/* ================= STEPS ================= */}
          {steps.length > 0 && (
            <>
              <h3 className="font-heading font-bold mb-4 mt-8 text-[20px] text-brand-dark tracking-[-0.01em]">
                {stepSectionTitle}
              </h3>

              <div className="space-y-4 mb-8">
                {steps.map((item) => (
                  <div
                    key={item.step}
                    className="flex gap-4 p-4 rounded-xl bg-brand-surface border-[1.5px] border-brand-border"
                  >
                    <div
                      className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 text-white font-extrabold text-[13px]"
                      style={{
                        background: stepCircleColor,
                      }}
                    >
                      {item.step}
                    </div>

                    <div>
                      <h4 className="font-heading font-bold text-sm text-brand-dark mb-1">
                        {item.title}
                      </h4>
                      <p className="text-sm text-brand-mid leading-relaxed">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}

          {/* ================= FEATURES ================= */}
          {features.length > 0 && (
            <>
              <h3 className="text-2xl font-bold mb-4">{featureSectionTitle}</h3>

              {featureIntro && <p className="text-gray-600 mb-4">{featureIntro}</p>}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
                {features.map((item) => (
                  <div
                    key={item.title}
                    className="flex gap-3 p-4 rounded-xl border border-brand-border"
                  >
                    <div
                      className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
                      style={{ background: item.bg || '#F3EEFF' }}
                    >
                      <Icon
                        name={item.icon}
                        size={16}
                        variant="solid"
                        style={{ color: item.color || '#7C5CBF' }}
                      />
                    </div>

                    <div>
                      <p className="font-heading font-bold text-sm text-brand-dark mb-1">
                        {item.title}
                      </p>
                      <p className="text-xs text-brand-mid leading-relaxed">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}

          {/* ================= USE CASES ================= */}
          {useCases && useCases.length > 0 && (
            <>
              <h3 className="text-2xl font-bold mb-4">{useCaseSectionTitle}</h3>

              {useCaseIntro && <p className="text-gray-600 mb-6">{useCaseIntro}</p>}

              <ul className="space-y-2">
                {useCases.map((item, i) => (
                  <li key={i} className="flex items-start gap-2.5">
                    <Icon
                      name="CheckCircleIcon"
                      size={15}
                      variant="solid"
                      style={{ color: stepCircleColor }}
                    />
                    <span className="text-sm text-brand-mid leading-relaxed">{item}</span>
                  </li>
                ))}
              </ul>
            </>
          )}

          {/* ================= SECURITY ================= */}
          {security && security.length > 0 && (
            <>
              <h3 className="text-2xl font-bold mt-8 mb-6">{securitySectionTitle}</h3>

              {security.map((p, i) => (
                <p key={i} className="mb-4 text-gray-600">
                  {p}
                </p>
              ))}
            </>
          )}
        </div>
      </article>

      {/* ================= FAQ ================= */}
      <section className="pb-16">
        <h2 className="font-heading font-extrabold mb-8 text-2xl">{faqSectionTitle}</h2>

        <FAQAccordion
          faqs={faqs.map((faq) => ({ question: faq.q, answer: faq.a }))}
          color={stepCircleColor}
        />
      </section>

      {/* ================= RELATED ================= */}
      <section className="pb-16">
        <h2 className="font-heading font-bold mb-6 text-lg">Other Free PDF Tools</h2>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredTools.map((tool) => (
            <Link
              key={tool.href}
              href={tool.href}
              title={`${tool.title} — ${tool.description}`}
              className="flex items-center gap-3 p-4 border border-brand-border rounded-xl hover:border-primary-mid transition-colors group"
            >
              <div
                className="w-10 h-10 shrink-0 aspect-square rounded-xl flex items-center justify-center group-hover:scale-105 transition-transform"
                style={{ background: `${tool.color}15` }}
              >
                <Icon name={tool.icon} size={18} variant="solid" style={{ color: tool.color }} />
              </div>

              <div>
                <p className="font-heading font-bold text-sm text-brand-dark transition-colors group-hover:text-primary">
                  {tool.title}
                </p>
                <p className="text-[11px] text-brand-muted line-clamp-1">{tool.description}</p>
              </div>
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
}
